// Generated from products/analytics/shared/jsonr.ts; run analytics generate:site-runtime.
/** Portable data-only profile of our JSONR wire markers. Call the owning Zod schema after parse. */
type Encoded = null | boolean | number | string | Encoded[] | { [key: string]: Encoded };
const maxBytes = 65536;
const maxDepth = 32;
const maxNodes = 10000;
const encoder = new TextEncoder();
const forbidden = new Set(["__proto__", "prototype", "constructor", "$date"]);

function reject(message: string): never {
  throw new TypeError(`Invalid analytics JSONR: ${message}`);
}
function checkKey(key: string): void {
  if (forbidden.has(key) || key.startsWith("$$$")) reject("reserved object key");
}
function budget() {
  let nodes = 0;
  return (depth: number) => {
    if (depth > maxDepth || ++nodes > maxNodes) reject("structural limit exceeded");
  };
}
function encode(
  value: unknown,
  depth: number,
  check: (depth: number) => void,
  ancestors: Set<object>,
): Encoded {
  check(depth);
  if (value === undefined) return { $$$undefined: true };
  if (value === null || typeof value === "boolean" || typeof value === "string") return value;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) reject("non-finite number");
    return value;
  }
  if (typeof value !== "object") return reject("unsupported value");
  if (ancestors.has(value)) reject("cyclic value");
  ancestors.add(value);
  try {
    if (value instanceof Date) {
      const time = Date.prototype.getTime.call(value);
      if (!Number.isFinite(time)) reject("invalid date");
      return { $$$date: time };
    }
    if (value instanceof Set) {
      if (value.size > maxNodes) reject("set too large");
      return {
        $$$set: Array.from(Set.prototype.values.call(value), (item) =>
          encode(item, depth + 1, check, ancestors),
        ),
      };
    }
    if (Array.isArray(value)) {
      if (value.length > maxNodes) reject("array too large");
      return Array.from({ length: value.length }, (_, index) => {
        const descriptor = Object.getOwnPropertyDescriptor(value, String(index));
        if (!descriptor || !("value" in descriptor)) return reject("sparse array or accessor");
        return encode(descriptor.value, depth + 1, check, ancestors);
      });
    }
    if (Object.getPrototypeOf(value) !== Object.prototype && Object.getPrototypeOf(value) !== null)
      reject("non-plain object");
    const result: { [key: string]: Encoded } = {};
    for (const key of Reflect.ownKeys(value)) {
      if (typeof key !== "string") reject("symbol key");
      checkKey(key);
      const descriptor = Object.getOwnPropertyDescriptor(value, key);
      if (!descriptor || !("value" in descriptor) || !descriptor.enumerable)
        reject("accessor or hidden field");
      result[key] = encode(descriptor.value, depth + 1, check, ancestors);
    }
    return result;
  } finally {
    ancestors.delete(value);
  }
}
function decode(value: unknown, depth: number, check: (depth: number) => void): unknown {
  check(depth);
  if (value === null || typeof value === "boolean" || typeof value === "string") return value;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) reject("non-finite number");
    return value;
  }
  if (Array.isArray(value)) return value.map((item) => decode(item, depth + 1, check));
  if (typeof value !== "object") return reject("unsupported value");
  const keys = Object.keys(value);
  const record = Object.getOwnPropertyDescriptors(value);
  const tagged = keys.some((key) => key.startsWith("$$$"));
  if (tagged) {
    if (keys.length !== 1) reject("marker with additional fields");
    const key = keys[0];
    if (key === undefined) return reject("empty marker");
    const field: unknown = record[key]?.value;
    if (key === "$$$undefined" && field === true) return undefined;
    if (
      key === "$$$date" &&
      typeof field === "number" &&
      Number.isSafeInteger(field) &&
      Math.abs(field) <= 8640000000000000
    )
      return new Date(field);
    if (key === "$$$set" && Array.isArray(field))
      return new Set(field.map((item) => decode(item, depth + 1, check)));
    return reject("unsupported or malformed marker");
  }
  const result: Record<string, unknown> = {};
  for (const key of keys) {
    checkKey(key);
    result[key] = decode(record[key]?.value, depth + 1, check);
  }
  return result;
}
export const JSONR = {
  stringify(value: unknown): string {
    const text = JSON.stringify(encode(value, 0, budget(), new Set()));
    if (encoder.encode(text).byteLength > maxBytes) reject("message too large");
    return text;
  },
  parse(text: string): unknown {
    if (encoder.encode(text).byteLength > maxBytes) reject("message too large");
    const value: unknown = JSON.parse(text);
    return decode(value, 0, budget());
  },
};
